import clsx from "clsx";
import { AlertTriangle, Brain, ChevronDown, ChevronLeft, ChevronRight, Pencil, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button, Textarea } from "@/components/ui";
import type { MessageStatus, Part, Usage } from "@/lib/types";
import AnswerStats from "./AnswerStats";
import { ACTION_BUTTON, ACTION_ICON } from "./actionStyles";
import Markdown, { CopyButton, type CodeAction, type CodeView } from "./Markdown";
import { ReadAloud } from "./Voice";

export interface ViewMessage {
  id: string;
  role: "user" | "assistant" | "system";
  parts: Part[];
  status: MessageStatus;
  error?: string | null;
  model_name?: string | null;
  usage?: Usage | null;
}

/** Splits <think>...</think> blocks inside text parts (models without a separate reasoning field). */
export function splitThink(parts: Part[]): Part[] {
  const out: Part[] = [];
  for (const p of parts) {
    if (p.type !== "text" || !p.text.includes("<think>")) {
      out.push(p);
      continue;
    }
    const re = /<think>([\s\S]*?)(<\/think>|$)/g;
    let last = 0;
    for (let m = re.exec(p.text); m; m = re.exec(p.text)) {
      if (m.index > last) out.push({ type: "text", text: p.text.slice(last, m.index) });
      out.push({ type: "reasoning", text: m[1] });
      last = m.index + m[0].length;
      if (!m[0]) break;
    }
    if (last < p.text.length) out.push({ type: "text", text: p.text.slice(last) });
  }
  return out;
}

export function plainText(parts: Part[]): string {
  return splitThink(parts)
    .filter((p) => p.type === "text")
    .map((p) => (p as { text: string }).text)
    .join("");
}

function Reasoning({ text, active }: { text: string; active: boolean }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const follow = useRef(true);
  // While still thinking, the open box follows its own end (unless scrolled up inside it).
  useEffect(() => {
    const el = box.current;
    if (el && active && follow.current) el.scrollTop = el.scrollHeight;
  }, [text, active, open]);
  return (
    <div className="my-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200"
      >
        <Brain size={14} className={clsx(active && "animate-pulse")} />
        {active ? "Thinking…" : "Thought process"}
        <ChevronDown size={14} className={clsx("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div
          ref={box}
          onScroll={(e) => {
            const el = e.currentTarget;
            follow.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
          }}
          className="mt-2 max-h-[50vh] overflow-y-auto overscroll-contain whitespace-pre-wrap border-l-2 border-zinc-200 pl-3 text-sm leading-6 text-zinc-500 dark:border-zinc-800"
        >
          {text.trim()}
        </div>
      )}
    </div>
  );
}

export interface PartRenderers {
  attachment?: (p: Extract<Part, { type: "attachment" }>, key: number) => ReactNode;
  tool_call?: (p: Extract<Part, { type: "tool_call" }>, key: number, messageId: string) => ReactNode;
  notice?: (p: Extract<Part, { type: "notice" }>, key: number) => ReactNode;
  codeAction?: CodeAction;
  codeView?: CodeView;
}

export function Parts({ parts, streaming, renderers, messageId = "" }: { parts: Part[]; streaming: boolean; renderers?: PartRenderers; messageId?: string }) {
  const split = splitThink(parts);
  return (
    <>
      {split.map((p, i) => {
        const last = i === split.length - 1;
        switch (p.type) {
          case "text":
            return p.text ? <Markdown key={i} text={p.text} streaming={streaming && last} codeAction={renderers?.codeAction} codeView={renderers?.codeView} /> : null;
          case "reasoning":
            return p.text.trim() ? <Reasoning key={i} text={p.text} active={streaming && last} /> : null;
          case "attachment":
            return renderers?.attachment?.(p, i) ?? null;
          case "tool_call":
            return renderers?.tool_call?.(p, i, messageId) ?? null;
          case "notice":
            return renderers?.notice?.(p, i) ?? null;
        }
        return null;
      })}
    </>
  );
}

export interface Branch {
  index: number;
  count: number;
  go: (delta: number) => void;
}

export default function MessageView({
  message,
  branch,
  onEdit,
  onRegenerate,
  renderers,
  busy,
}: {
  message: ViewMessage;
  branch?: Branch;
  onEdit?: (text: string) => void;
  onRegenerate?: () => void;
  renderers?: PartRenderers;
  busy?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const streaming = message.status === "streaming";
  const text = plainText(message.parts);
  const isUser = message.role === "user";

  const nav = branch && branch.count > 1 && (
    <div className="flex items-center text-xs text-zinc-500 pointer-coarse:text-sm">
      <button type="button" className={ACTION_BUTTON} disabled={branch.index === 0 || busy} onClick={() => branch.go(-1)} aria-label="Previous version">
        <ChevronLeft className={ACTION_ICON} />
      </button>
      <span className="tabular-nums">
        {branch.index + 1}/{branch.count}
      </span>
      <button type="button" className={ACTION_BUTTON} disabled={branch.index === branch.count - 1 || busy} onClick={() => branch.go(1)} aria-label="Next version">
        <ChevronRight className={ACTION_ICON} />
      </button>
    </div>
  );

  if (isUser) {
    return (
      <div className="group flex flex-col items-end gap-1">
        {editing ? (
          <div className="w-full max-w-[85%] space-y-2">
            <Textarea
              autoFocus
              rows={Math.min(12, Math.max(3, draft.split("\n").length))}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  setEditing(false);
                  onEdit?.(draft);
                }
                if (e.key === "Escape") setEditing(false);
              }}
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setEditing(false);
                  onEdit?.(draft);
                }}
              >
                Send
              </Button>
            </div>
          </div>
        ) : (
          <>
            {message.parts.some((p) => p.type === "attachment") && (
              <div className="flex max-w-[85%] flex-wrap justify-end">
                <Parts parts={message.parts.filter((p) => p.type === "attachment")} streaming={false} renderers={renderers} />
              </div>
            )}
            {message.parts.some((p) => p.type === "text" && p.text) && (
              <div className="max-w-[85%] rounded-2xl bg-zinc-100 px-4 py-2.5 dark:bg-zinc-800">
                <Parts parts={message.parts.filter((p) => p.type !== "attachment")} streaming={false} renderers={renderers} />
              </div>
            )}
          </>
        )}
        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
          {nav}
          <CopyButton text={text} className={ACTION_BUTTON} iconClassName={ACTION_ICON} />
          {onEdit && !editing && (
            <button
              type="button"
              className={ACTION_BUTTON}
              onClick={() => {
                setDraft(text);
                setEditing(true);
              }}
              disabled={busy}
              aria-label="Edit"
              title="Edit"
            >
              <Pencil className={ACTION_ICON} />
            </button>
          )}
        </div>
      </div>
    );
  }

  const empty = !message.parts.length;
  return (
    <div className="group">
      {empty && streaming && (
        <div className="flex h-7 items-center gap-1">
          <span className="h-2 w-2 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.3s]" />
          <span className="h-2 w-2 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.15s]" />
          <span className="h-2 w-2 animate-bounce rounded-full bg-zinc-400" />
        </div>
      )}
      <Parts parts={message.parts} streaming={streaming} renderers={renderers} messageId={message.id} />
      {message.status === "error" && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="break-words">{message.error || "Something went wrong"}</span>
        </div>
      )}
      {message.status === "cancelled" && <p className="mt-1 text-xs text-zinc-500">Stopped</p>}
      {!streaming && (
        <div className="mt-1 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
          {nav}
          <CopyButton text={text} className={ACTION_BUTTON} iconClassName={ACTION_ICON} />
          <ReadAloud text={text} />
          {onRegenerate && (
            <button
              type="button"
              className={ACTION_BUTTON}
              onClick={onRegenerate}
              disabled={busy}
              aria-label="Regenerate"
              title="Regenerate"
            >
              <RefreshCw className={ACTION_ICON} />
            </button>
          )}
          <AnswerStats usage={message.usage} />
          {message.model_name && <span className="ml-1 text-xs text-zinc-400">{message.model_name}</span>}
        </div>
      )}
    </div>
  );
}
